import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './SuperAdminDashboard.css';
import './SuperAdminMobileMenu.css';

function Schools() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        toggleRef.current &&
        !menuRef.current.contains(event.target) &&
        !toggleRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingSchool, setEditingSchool] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  // Delete popup states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedSchoolName, setDeletedSchoolName] = useState('');
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch schools data from Supabase
  const fetchSchools = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { data, error } = await supabase
        .from('schools')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching schools:', error);
        setError('Failed to load schools data');
        return;
      }
      
      setSchools(data || []);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load schools data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const handleLogout = async () => {
    try {
      // Sign out from Supabase if logged in
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.auth.signOut();
      }
      
      // Clear localStorage
      localStorage.removeItem('isSuperAdminAuthenticated');
      
      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear localStorage and redirect even if there's an error
      localStorage.removeItem('isSuperAdminAuthenticated');
      navigate('/');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getSchoolTypeBadge = (type) => {
    const typeColors = {
      'public': 'blue',
      'private': 'green',
      'charter': 'orange',
      'international': 'purple'
    };
    
    return (
      <span className={`type-badge ${typeColors[type] || 'gray'}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  // Edit School Functions
  const handleEditSchool = (school) => {
    setEditingSchool(school);
    setEditForm({
      school_name: school.school_name || '',
      email: school.email || '',
      password: '', // Don't pre-fill password
      school_type: school.school_type || 'public',
      address: school.address || '',
      city: school.city || '',
      state: school.state || '',
      zip_code: school.zip_code || '',
      phone: school.phone || '',
      website: school.website || '',
      total_students: school.total_students || 0,
      total_teachers: school.total_teachers || 0,
      is_active: school.is_active !== false
    });
    setEditErrors({});
    setShowEditPassword(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.school_name.trim()) {
      errors.school_name = 'School name is required';
    }
    
    if (!editForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(editForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    // No password length restriction
    
    if (!editForm.school_type) {
      errors.school_type = 'School type is required';
    }
    
    if (!editForm.address.trim()) {
      errors.address = 'Address is required';
    }
    
    if (!editForm.city.trim()) {
      errors.city = 'City is required';
    }
    
    if (!editForm.state.trim()) {
      errors.state = 'State is required';
    }
    
    if (!editForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (editForm.total_students < 0) {
      errors.total_students = 'Student count cannot be negative';
    }
    
    if (editForm.total_teachers < 0) {
      errors.total_teachers = 'Teacher count cannot be negative';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const updateData = {
        school_name: editForm.school_name.trim(),
        email: editForm.email.trim(),
        school_type: editForm.school_type,
        address: editForm.address.trim(),
        city: editForm.city.trim(),
        state: editForm.state.trim(),
        zip_code: editForm.zip_code.trim() || null,
        phone: editForm.phone.trim(),
        website: editForm.website.trim() || null,
        total_students: parseInt(editForm.total_students) || 0,
        total_teachers: parseInt(editForm.total_teachers) || 0,
        is_active: editForm.is_active
        // Don't include updated_at - let the trigger handle it
      };

      // Update the school data first (without password)
      const { error } = await supabase
        .from('schools')
        .update(updateData)
        .eq('id', editingSchool.id);
      
      if (error) {
        console.error('Error updating school:', error);
        alert(`Failed to update school: ${error.message || 'Please check your data and try again.'}`);
        setIsSubmitting(false);
        return;
      }
      
      // Update password separately if provided
      if (editForm.password && editForm.password.trim() !== '') {
        try {
          console.log('Updating school password...');
          
          // First, update password_hash in schools table (for backward compatibility)
          const passwordHash = btoa(editForm.password.trim()); // Base64 encoding as simple demo
          const { error: passwordHashError } = await supabase
            .from('schools')
            .update({ password_hash: passwordHash })
            .eq('id', editingSchool.id);
          
          if (passwordHashError) {
            console.warn('Failed to update password_hash in schools table:', passwordHashError);
          }
          
          // Then, update password in Supabase Auth if auth_user_id exists
          const { data: schoolData, error: schoolError } = await supabase
            .from('schools')
            .select('auth_user_id')
            .eq('id', editingSchool.id)
            .single();
          
          if (!schoolError && schoolData && schoolData.auth_user_id) {
            try {
              // Update password using Supabase Auth Admin API
              const { error: passwordError } = await supabase.auth.admin.updateUserById(
                schoolData.auth_user_id,
                { password: editForm.password.trim() }
              );
              
              if (passwordError) {
                console.warn('Failed to update school password in Auth:', passwordError);
                // Don't show error to user - password_hash was updated, Auth update is optional
              } else {
                console.log('School password updated successfully in Auth');
              }
            } catch (authErr) {
              console.warn('Error updating school password in Auth:', authErr);
              // Don't show error - password_hash was updated
            }
          } else {
            console.log('School does not have auth_user_id, skipping Auth password update');
          }
        } catch (passwordErr) {
          console.warn('Error updating school password:', passwordErr);
          // Don't fail the whole update - password update is optional
        }
      }
      
      // Refresh the schools list
      await fetchSchools();
      
      // Close the edit popup
      setEditingSchool(null);
      setEditForm({});
      setEditErrors({});
      
      alert('School updated successfully!');
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update school. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchool = (school) => {
    setSchoolToDelete(school);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!schoolToDelete) return;
    
    setIsDeleting(true);
    setShowDeleteConfirm(false);
    
    try {
      const { error } = await supabase
        .from('schools')
        .delete()
        .eq('id', schoolToDelete.id);
      
      if (error) {
        console.error('Error deleting school:', error);
        setDeleteErrorMessage(error.message || 'Failed to delete school. Please try again.');
        setShowDeleteError(true);
        setIsDeleting(false);
        return;
      }
      
      // Store the deleted school name for success message
      const deletedName = schoolToDelete.school_name;
      setDeletedSchoolName(deletedName);
      
      // Refresh the schools list
      await fetchSchools();
      
      // Show success popup
      setShowDeleteSuccess(true);
      setIsDeleting(false);
      setSchoolToDelete(null);
      
    } catch (err) {
      console.error('Error:', err);
      setDeleteErrorMessage('An unexpected error occurred. Please try again.');
      setShowDeleteError(true);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSchoolToDelete(null);
  };

  const closeEditPopup = () => {
    setEditingSchool(null);
    setEditForm({});
    setEditErrors({});
    setShowEditPassword(false);
  };

  return (
    <div className="super-admin-dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-left">
          <span className="mobile-header-text">Super Admin</span>
        </div>
        <button 
          ref={toggleRef}
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Sidebar - Mobile Dropdown Menu */}
      <div ref={menuRef} className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy App Logo" 
            className="sidebar-logo"
          />
        </div>
        
        <nav className="sidebar-nav">
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/dashboard');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Dashboard</span>
          </button>
          
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/add-school');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Add School</span>
          </button>
          
          <button
            className="nav-item active"
            onClick={() => {
              navigate('/superadmin/schools');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Schools</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/terms-and-conditions');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Terms & Conditions</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/privacy-policy');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Privacy Policy / EULA</span>
          </button>
        </nav>
        
        <div className="sidebar-spacer"></div>
        
        <div className="sidebar-footer">
          <button 
            className="logout-btn" 
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
          >
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Schools Management</h1>
          <div className="user-info">
            <span>Super Admin</span>
            <div className="user-avatar">👑</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="schools-content">
            <div className="schools-header">
              <div className="schools-stats">
                <div className="stat-item">
                  <span className="stat-number">{schools.length}</span>
                  <span className="stat-label">Total Schools</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{schools.filter(s => s.is_active).length}</span>
                  <span className="stat-label">Active Schools</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{schools.reduce((sum, s) => sum + (s.total_students || 0), 0)}</span>
                  <span className="stat-label">Total Students</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner">⏳</div>
                <p>Loading schools data...</p>
              </div>
            ) : schools.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🏫</div>
                <h3>No Schools Found</h3>
                <p>No schools have been added yet. Click "Add School" to get started.</p>
                <button 
                  className="add-school-btn"
                  onClick={() => navigate('/superadmin/add-school')}
                >
                  Add First School
                </button>
              </div>
            ) : (
              <div className="schools-table-container">
                <table className="schools-table">
                   <thead>
                     <tr>
                       <th>School Name</th>
                       <th>Type</th>
                       <th>Email</th>
                       <th>Students</th>
                       <th>Teachers</th>
                       <th>Actions</th>
                     </tr>
                   </thead>
                  <tbody>
                    {schools.map((school) => (
                      <tr key={school.id}>
                        <td>
                          <div className="school-name">
                            <strong>{school.school_name}</strong>
                            {school.website && (
                              <a 
                                href={school.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="website-link"
                              >
                                🌐
                              </a>
                            )}
                          </div>
                        </td>
                        <td>{getSchoolTypeBadge(school.school_type)}</td>
                        <td>
                          <a href={`mailto:${school.email}`} className="email-link">
                            {school.email}
                          </a>
                        </td>
                         <td>{school.total_students || 0}</td>
                         <td>{school.total_teachers || 0}</td>
                         <td>
                           <div className="action-buttons">
                             <button 
                               className="edit-btn"
                               onClick={() => handleEditSchool(school)}
                               title="Edit School"
                             >
                               ✏️
                             </button>
                             <button 
                               className="delete-btn"
                               onClick={() => handleDeleteSchool(school)}
                               title="Delete School"
                             >
                               🗑️
                             </button>
                           </div>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
             )}
           </div>
         </div>
       </div>

       {/* Edit School Popup */}
       {editingSchool && (
         <div className="popup-overlay">
           <div className="popup-content">
             <div className="popup-header">
               <h2 style={{ color: '#FFFFFF' }}>Edit School</h2>
               <button className="close-btn" onClick={closeEditPopup}>
                 ✕
               </button>
             </div>
             
             <form onSubmit={handleEditSubmit} className="edit-school-form">
               <div className="form-section">
                 <h3>Basic Information</h3>
                 <div className="form-row">
                   <div className="form-group">
                     <label htmlFor="edit_school_name">School Name *</label>
                     <input
                       type="text"
                       id="edit_school_name"
                       name="school_name"
                       value={editForm.school_name}
                       onChange={handleEditInputChange}
                       className={editErrors.school_name ? 'error' : ''}
                     />
                     {editErrors.school_name && (
                       <span className="error-text">{editErrors.school_name}</span>
                     )}
                   </div>
                   
                   <div className="form-group">
                     <label htmlFor="edit_email">Email *</label>
                     <input
                       type="email"
                       id="edit_email"
                       name="email"
                       value={editForm.email}
                       onChange={handleEditInputChange}
                       className={editErrors.email ? 'error' : ''}
                     />
                     {editErrors.email && (
                       <span className="error-text">{editErrors.email}</span>
                     )}
                   </div>
                 </div>
                 
                 <div className="form-row">
                   <div className="form-group">
                     <label htmlFor="edit_password">Change Password (leave blank to keep current)</label>
                     <div className="password-input-wrapper" style={{ position: 'relative', width: '100%' }}>
                       <input
                         type={showEditPassword ? "text" : "password"}
                         id="edit_password"
                         name="password"
                         value={editForm.password}
                         onChange={handleEditInputChange}
                         className={editErrors.password ? 'error' : ''}
                         placeholder="Enter new password or leave blank"
                         style={{ 
                           paddingRight: '45px',
                           width: '100%',
                           boxSizing: 'border-box'
                         }}
                       />
                       <button
                         type="button"
                         className="password-toggle-icon"
                         onClick={() => setShowEditPassword(!showEditPassword)}
                         style={{
                           position: 'absolute',
                           right: '12px',
                           top: '50%',
                           transform: 'translateY(-50%)',
                           background: 'none',
                           border: 'none',
                           cursor: 'pointer',
                           padding: '0',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           fontSize: '20px',
                           width: '24px',
                           height: '24px',
                           zIndex: 10,
                           color: '#6B7280'
                         }}
                         aria-label={showEditPassword ? "Hide password" : "Show password"}
                       >
                         {showEditPassword ? '🙈' : '👁️'}
                       </button>
                     </div>
                     {editErrors.password && (
                       <span className="error-text">{editErrors.password}</span>
                     )}
                     <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                       Leave blank to keep the current password unchanged
                     </small>
                   </div>
                   
                   <div className="form-group">
                     <label htmlFor="edit_school_type">School Type *</label>
                     <select
                       id="edit_school_type"
                       name="school_type"
                       value={editForm.school_type}
                       onChange={handleEditInputChange}
                       className={editErrors.school_type ? 'error' : ''}
                     >
                       <option value="public">Public</option>
                       <option value="private">Private</option>
                       <option value="charter">Charter</option>
                       <option value="international">International</option>
                     </select>
                     {editErrors.school_type && (
                       <span className="error-text">{editErrors.school_type}</span>
                     )}
                   </div>
                 </div>
               </div>

               <div className="form-section">
                 <h3>Contact Information</h3>
                 <div className="form-group">
                   <label htmlFor="edit_address">Address *</label>
                   <input
                     type="text"
                     id="edit_address"
                     name="address"
                     value={editForm.address}
                     onChange={handleEditInputChange}
                     className={editErrors.address ? 'error' : ''}
                   />
                   {editErrors.address && (
                     <span className="error-text">{editErrors.address}</span>
                   )}
                 </div>
                 
                 <div className="form-row">
                   <div className="form-group">
                     <label htmlFor="edit_city">City *</label>
                     <input
                       type="text"
                       id="edit_city"
                       name="city"
                       value={editForm.city}
                       onChange={handleEditInputChange}
                       className={editErrors.city ? 'error' : ''}
                     />
                     {editErrors.city && (
                       <span className="error-text">{editErrors.city}</span>
                     )}
                   </div>
                   
                   <div className="form-group">
                     <label htmlFor="edit_state">State *</label>
                     <input
                       type="text"
                       id="edit_state"
                       name="state"
                       value={editForm.state}
                       onChange={handleEditInputChange}
                       className={editErrors.state ? 'error' : ''}
                     />
                     {editErrors.state && (
                       <span className="error-text">{editErrors.state}</span>
                     )}
                   </div>
                   
                   <div className="form-group">
                     <label htmlFor="edit_zip_code">ZIP Code</label>
                     <input
                       type="text"
                       id="edit_zip_code"
                       name="zip_code"
                       value={editForm.zip_code}
                       onChange={handleEditInputChange}
                     />
                   </div>
                 </div>
                 
                 <div className="form-row">
                   <div className="form-group">
                     <label htmlFor="edit_phone">Phone *</label>
                     <input
                       type="tel"
                       id="edit_phone"
                       name="phone"
                       value={editForm.phone}
                       onChange={handleEditInputChange}
                       className={editErrors.phone ? 'error' : ''}
                     />
                     {editErrors.phone && (
                       <span className="error-text">{editErrors.phone}</span>
                     )}
                   </div>
                   
                   <div className="form-group">
                     <label htmlFor="edit_website">Website</label>
                     <input
                       type="url"
                       id="edit_website"
                       name="website"
                       value={editForm.website}
                       onChange={handleEditInputChange}
                       placeholder="https://example.com"
                     />
                   </div>
                 </div>
               </div>

               <div className="form-section">
                 <h3>School Statistics</h3>
                 <div className="form-row">
                   <div className="form-group">
                     <label htmlFor="edit_total_students">Total Students</label>
                     <input
                       type="number"
                       id="edit_total_students"
                       name="total_students"
                       value={editForm.total_students}
                       onChange={handleEditInputChange}
                       min="0"
                       className={editErrors.total_students ? 'error' : ''}
                     />
                     {editErrors.total_students && (
                       <span className="error-text">{editErrors.total_students}</span>
                     )}
                   </div>
                   
                   <div className="form-group">
                     <label htmlFor="edit_total_teachers">Total Teachers</label>
                     <input
                       type="number"
                       id="edit_total_teachers"
                       name="total_teachers"
                       value={editForm.total_teachers}
                       onChange={handleEditInputChange}
                       min="0"
                       className={editErrors.total_teachers ? 'error' : ''}
                     />
                     {editErrors.total_teachers && (
                       <span className="error-text">{editErrors.total_teachers}</span>
                     )}
                   </div>
                 </div>
                 
                 <div className="form-group">
                   <label className="checkbox-label">
                     <input
                       type="checkbox"
                       name="is_active"
                       checked={editForm.is_active}
                       onChange={handleEditInputChange}
                     />
                     Active School
                   </label>
                 </div>
               </div>

               <div className="form-actions">
                 <button 
                   type="button" 
                   className="cancel-btn"
                   onClick={closeEditPopup}
                   disabled={isSubmitting}
                 >
                   Cancel
                 </button>
                 <button 
                   type="submit" 
                   className="submit-btn"
                   disabled={isSubmitting}
                 >
                   {isSubmitting ? 'Updating...' : 'Update School'}
                 </button>
               </div>
             </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && schoolToDelete && (
        <div
          className="delete-confirm-popup-overlay"
          onClick={cancelDelete}
        >
          <div
            className="delete-confirm-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-confirm-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#EF4444" opacity="0.2"/>
                <path d="M9 9L15 15M15 9L9 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="delete-confirm-popup-title">Confirm Deletion</h2>
            <div className="delete-confirm-popup-message">
              <p>Are you sure you want to delete <strong>"{schoolToDelete.school_name}"</strong>?</p>
              <p style={{ marginTop: '10px', color: '#EF4444', fontWeight: '600' }}>This action cannot be undone.</p>
            </div>
            <div className="delete-confirm-popup-actions">
              <button
                type="button"
                className="delete-confirm-popup-btn cancel"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-confirm-popup-btn confirm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete School'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Popup */}
      {showDeleteSuccess && (
        <div
          className="success-popup-overlay"
          onClick={() => setShowDeleteSuccess(false)}
        >
          <div
            className="success-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" opacity="0.2"/>
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-popup-title">School Deleted Successfully!</h2>
            <div className="success-popup-details">
              <div className="success-detail-item">
                <span className="success-detail-label">School Name:</span>
                <span className="success-detail-value">{deletedSchoolName}</span>
              </div>
              <div className="success-detail-item">
                <span className="success-detail-label">Status:</span>
                <span className="success-detail-value" style={{ color: '#10B981', fontWeight: '600' }}>Deleted</span>
              </div>
            </div>
            <div className="success-popup-actions">
              <button
                type="button"
                className="success-popup-btn primary"
                onClick={() => setShowDeleteSuccess(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Error Popup */}
      {showDeleteError && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowDeleteError(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#EF4444" opacity="0.2"/>
                <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="error-popup-title">Error Deleting School</h2>
            <div className="error-popup-message">
              <p>{deleteErrorMessage}</p>
            </div>
            <div className="error-popup-actions">
              <button
                type="button"
                className="error-popup-btn"
                onClick={() => setShowDeleteError(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Schools;
